import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Pencil, Trash2, Stethoscope } from "lucide-react";
import { toast } from "sonner";

const empty = { full_name: "", specialty: "", clinic: "", contact: "", address: "", notes: "" };

export default function ReferralPartners() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async (search = "") => {
    const { data } = await api.get(`/referral-partners${search ? `?q=${encodeURIComponent(search)}` : ""}`);
    setItems(Array.isArray(data) ? data : []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q]);

  const openNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      full_name: p.full_name || "", specialty: p.specialty || "", clinic: p.clinic || "",
      contact: p.contact || "", address: p.address || "", notes: p.notes || "",
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.full_name?.trim()) { toast.error("Doctor name is required"); return; }
    try {
      if (editingId) {
        await api.put(`/referral-partners/${editingId}`, form);
        toast.success("Partner updated");
      } else {
        await api.post("/referral-partners", form);
        toast.success("Partner added");
      }
      setOpen(false);
      load(q);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Archive this referral partner?")) return;
    await api.delete(`/referral-partners/${id}`);
    toast.success("Archived");
    load(q);
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl" data-testid="partners-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Referral Partners</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} partner{items.length === 1 ? "" : "s"} — saved doctors you can quickly select on referral forms.</p>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-add-partner">
          <Plus size={14} className="mr-2" /> Add Partner
        </Button>
      </div>

      <Card className="p-4 border-slate-200 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name, specialty or clinic..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="partner-search" />
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Doctor</th>
                <th className="px-4 py-3 text-left font-semibold">Specialty</th>
                <th className="px-4 py-3 text-left font-semibold">Clinic / Hospital</th>
                <th className="px-4 py-3 text-left font-semibold">Contact</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No referral partners yet.</td></tr>
              ) : items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`partner-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-blue-50 text-blue-700 flex items-center justify-center"><Stethoscope size={14} /></div>
                      <div className="font-medium text-slate-900">{p.full_name}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{p.specialty || "—"}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs">
                    <div>{p.clinic || "—"}</div>
                    {p.address ? <div className="text-slate-500">{p.address}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-700 mono text-xs">{p.contact || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`partner-edit-${p.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`partner-delete-${p.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="partner-dialog">
          <DialogHeader><DialogTitle>{editingId ? "Edit Partner" : "Add Referral Partner"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Doctor Name *</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Dr. Juan Santos" data-testid="partner-input-name" /></div>
            <div><Label>Specialty</Label><Input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="Cardiology" data-testid="partner-input-specialty" /></div>
            <div><Label>Contact</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="+63 9XX XXX XXXX" data-testid="partner-input-contact" /></div>
            <div className="col-span-2"><Label>Clinic / Hospital</Label><Input value={form.clinic} onChange={(e) => setForm({ ...form, clinic: e.target.value })} data-testid="partner-input-clinic" /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal note (e.g. preferred hours)" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="partner-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
